import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import StoreNavigation from '../components/StoreNavigation';
import StoreFooter from '../components/StoreFooter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { User, Package, Heart, Settings, LogIn, UserPlus, ArrowRight } from 'lucide-react';

const Account = () => {
  const { getCartItemCount } = useCart();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('login');
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    firstName: '', 
    lastName: '', 
    email: '', 
    password: '', 
    confirmPassword: '' 
  });

  // Mock - in production this would check real auth state
  const isLoggedIn = false;

  const handleLogin = (e) => {
    e.preventDefault();
    toast({
      title: "Coming Soon!",
      description: "Account login will be available when we launch our full store.",
    });
  };

  const handleRegister = (e) => {
    e.preventDefault();
    toast({
      title: "Coming Soon!",
      description: "Account registration will be available when we launch our full store.",
    });
  };

  if (isLoggedIn) {
    // Logged in view - would show orders, profile, etc.
    return (
      <div className="min-h-screen bg-white">
        <StoreNavigation cartItemCount={getCartItemCount()} />

        <div className="pt-32 pb-24">
          <div className="container mx-auto px-6">
            <h1 className="text-4xl font-black text-[#004D51] mb-8">My Account</h1>
            {/* Account dashboard would go here */}
          </div>
        </div>

        <StoreFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <StoreNavigation cartItemCount={getCartItemCount()} />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#004D51] to-[#003338] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <User className="w-16 h-16 mx-auto mb-6 text-[#F39C49]" />
            <h1 className="text-5xl md:text-6xl font-black mb-6">
              Your <span className="text-[#F39C49]">Account</span>
            </h1>
            <p className="text-xl text-[#CFD2D4]">
              Sign in to track orders, save your favorites, and manage your profile.
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-16">
        <div className="max-w-md mx-auto">
          {/* Tab Switcher */}
          <div className="flex mb-8 bg-[#F3CEB2]/20 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('login')}
              className={`flex-1 py-3 px-4 rounded-lg font-bold transition-all flex items-center justify-center ${
                activeTab === 'login'
                  ? 'bg-[#004D51] text-white'
                  : 'text-[#545759] hover:text-[#004D51]'
              }`}
            >
              <LogIn className="w-4 h-4 mr-2" />
              Sign In
            </button>
            <button
              onClick={() => setActiveTab('register')}
              className={`flex-1 py-3 px-4 rounded-lg font-bold transition-all flex items-center justify-center ${
                activeTab === 'register'
                  ? 'bg-[#004D51] text-white'
                  : 'text-[#545759] hover:text-[#004D51]'
              }`}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create Account
            </button>
          </div>

          {/* Login Form */}
          {activeTab === 'login' && (
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-black text-[#004D51] mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                  placeholder="your@email.com"
                  data-testid="login-email"
                />
              </div>

              <div>
                <label className="block text-sm font-black text-[#004D51] mb-2">
                  Password
                </label>
                <Input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                  placeholder="••••••••"
                  data-testid="login-password"
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-[#545759]">Remember me</span>
                </label>
                <button type="button" className="text-sm text-[#DB5A14] hover:text-[#F39C49] font-bold">
                  Forgot password?
                </button>
              </div>

              <Button 
                type="submit"
                className="w-full h-14 bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black text-lg"
                data-testid="login-submit"
              >
                Sign In <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </form>
          )}

          {/* Register Form */}
          {activeTab === 'register' && (
            <form onSubmit={handleRegister} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    First Name
                  </label>
                  <Input
                    value={registerData.firstName}
                    onChange={(e) => setRegisterData({ ...registerData, firstName: e.target.value })}
                    required
                    className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                    placeholder="First"
                    data-testid="register-firstname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Last Name
                  </label>
                  <Input
                    value={registerData.lastName}
                    onChange={(e) => setRegisterData({ ...registerData, lastName: e.target.value })}
                    required
                    className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                    placeholder="Last"
                    data-testid="register-lastname"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-black text-[#004D51] mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  required
                  className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                  placeholder="your@email.com"
                  data-testid="register-email"
                />
              </div>

              <div>
                <label className="block text-sm font-black text-[#004D51] mb-2">
                  Password
                </label>
                <Input
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  required
                  className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                  placeholder="••••••••"
                  data-testid="register-password"
                />
              </div>

              <div>
                <label className="block text-sm font-black text-[#004D51] mb-2">
                  Confirm Password
                </label>
                <Input
                  type="password"
                  value={registerData.confirmPassword}
                  onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                  required
                  className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                  placeholder="••••••••"
                  data-testid="register-confirm-password"
                />
              </div>

              <Button 
                type="submit"
                className="w-full h-14 bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black text-lg"
                data-testid="register-submit"
              >
                Create Account <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </form>
          )}

          {/* Benefits */}
          <div className="mt-12 p-6 bg-[#F3CEB2]/20 rounded-xl">
            <h3 className="font-black text-[#004D51] mb-4">Account Benefits:</h3>
            <ul className="space-y-3">
              <li className="flex items-center text-[#545759]">
                <Package className="w-5 h-5 text-[#DB5A14] mr-3" />
                Track your orders
              </li>
              <li className="flex items-center text-[#545759]">
                <Heart className="w-5 h-5 text-[#DB5A14] mr-3" />
                Save your favorites
              </li>
              <li className="flex items-center text-[#545759]">
                <Settings className="w-5 h-5 text-[#DB5A14] mr-3" />
                Faster checkout
              </li>
            </ul>
          </div>
        </div>
      </div>

      <StoreFooter />
    </div>
  );
};

export default Account;
